import {EventEmitter, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {QUERY_FIELDS} from './url.service.data';
import {debounceTime, distinctUntilChanged, tap} from 'rxjs/operators';
import {Subject} from 'rxjs/Subject';

@Injectable()
export class UrlService {
    onUrlChange = new Subject<any>();
    instantOnUrlChange = new Subject<any>();
    onUrlChangeEmitter: EventEmitter<any> = new EventEmitter();
    routeChanged: EventEmitter<any> = new EventEmitter();
    queryParams;
    defaultPage = 1;
    defaultPerPage = 15;
    defaultOrderBy: Object = {
        'residents': 'suite.suiteNumber|asc',
        'units': 'suiteNumber|asc',
        'parking-spots': 'suite.suiteNumber|asc',
        'lockers': 'suite.suiteNumber|asc',
        'vehicles': 'suite.suiteNumber|asc',
        'pets': 'suite.suiteNumber|asc',
        'bike-racks': 'suite.suiteNumber|asc',
        'vendors': 'name|asc',
        'allParcels': 'createdAt|desc',
        'allIncidentReports': 'createdAt|desc',
        'allKeyLogs': 'keyTag|asc',
        'allClassifieds': 'title|asc',
        'allPassOnLogs': 'createdAt|desc',
        'email': 'createdAt|desc',
        'allDailyReports': 'startDate|desc',
        'themes': 'id|asc',
        'companies': 'id|asc',
        'allServiceRequests': 'createdAt|desc',
        'allAmenities': 'createdAt|desc',
        'allSettingsDirectory': 'id|asc',
        'allSettingsAmenities': 'createdAt|desc',
        'allParkingPasses': 'createdAt|desc',
        'allSettingsParcels': 'corporationNumberName|asc',
        'rules': 'createdAt|desc',
        'lots': 'createdAt|desc',
        'user-role': 'createdAt|desc',
        'user': 'id|asc',
        'property': 'id|asc',
        'propertyGroup': 'id|asc',
        'websiteBuilder': 'corporationNumber|asc',
        'allReports': 'createdAt|desc',
        'allTemplates': 'createdAt|desc',
        'allServiceRequestSettings': 'createdAt|desc',
        'allIncidentReportsSettings': 'createdAt|desc',
        'updates': 'createdAt|desc',
        'allSettingsPassOnLog': 'corporationNumberBdId|asc',
        'allStripeTransactions': 'createdAt|desc',
        'allStripePayouts': 'createdAt|desc'
    };
    buildingFilter: Object = {
        'residents': 'suite_building',
        'units': 'building_id',
        'parking-spots': 'building_id',
        'lockers': 'building_id',
        'vehicles': 'building_id',
        'pets': 'building_id',
        'bike-racks': 'building_id',
        'allParcels': 'building_id',
        'allIncidentReports': 'building_id',
        'allKeyLogs': 'building_id',
        'allClassifieds': 'building_id',
        'allPassOnLogs': 'building_id',
        'allDailyReports': 'building_id',
        'email': 'building_id[0]',
        'document': 'building_id',
        'allServiceRequests': 'building_id',
        'allSettingsAmenities': 'building_id',
        'rules': 'building_id',
        'lots': 'building_id',
        'allAmenities': 'building_id',
        'allParkingPasses': 'building_id',
        'allSettingsParcels': 'building_id',
        'allReports': 'building_id',
        'allTemplates': 'building_id',
        'user': 'building_id',
        'allServiceRequestSettings': 'building_id',
        'allIncidentReportsSettings': 'building_id',
        'updates': 'building_id',
    };

    queryFields = JSON.parse(JSON.stringify(QUERY_FIELDS));

    private currentQuery: string = '';

    modalOpened: EventEmitter<any> = new EventEmitter();
    modalClosed: EventEmitter<any> = new EventEmitter();

    public static EnableResetQueryParams = true;

    constructor(public router: Router) {
        this.onUrlChangeEmitter
            .pipe(
                tap(v => this.instantOnUrlChange.next(v)),
                distinctUntilChanged(),
                debounceTime(200),
                tap(v => this.onUrlChange.next(v))
            )
            .subscribe();
        this.getQueryParams();
    }

    getQueryParams(): void {
        this.queryParams = this.router.parseUrl(this.router.url).queryParams;
    }

    getQueryParamsJson(): any {
        return this.router.parseUrl(this.router.url).queryParams;
    }

    setQueryParams(tab?: string, page?: number, perPage?: number, orderBy?: string): void {
        if(perPage) {
            localStorage.setItem('perPage', perPage.toString());
        }

        const queryParams: any = {
            'tab': tab || this.queryParams['tab'],
            'page': page || this.queryParams['page'] || this.defaultPage,
            'per-page': localStorage.getItem('perPage') || perPage || this.queryParams['per-page'] || this.defaultPerPage,
            'order-by': orderBy || this.queryParams['order-by'] || this.defaultOrderBy[tab],
        };

        // FIXME: Use array to filter params.
        if (this.queryParams['query'])          queryParams['query']           = this.queryParams['query'];
        if (this.queryParams['building'])       queryParams['building']        = this.queryParams['building'];
        if (this.queryParams['inOut'])          queryParams['inOut']           = this.queryParams['inOut'];
        if (this.queryParams['report_type'])    queryParams['report_type']     = this.queryParams['report_type'];
        if (this.queryParams['status'])         queryParams['status']          = this.queryParams['status'];
        if (this.queryParams['report_type'])    queryParams['report_type']     = this.queryParams['report_type'];
        if (this.queryParams['priority'])       queryParams['priority']        = this.queryParams['priority'];
        if (this.queryParams['is_shared'])      queryParams['is_shared']       = this.queryParams['is_shared'];
        if (this.queryParams['is_public'])      queryParams['is_public']       = this.queryParams['is_public'];
        if (this.queryParams['permissions'])    queryParams['permissions']     = this.queryParams['permissions'];
        if (this.queryParams['user_type'])      queryParams['user_type']       = this.queryParams['user_type'];
        if (this.queryParams['enable_status'])  queryParams['enable_status']   = this.queryParams['enable_status'];
        if (this.queryParams['type'])           queryParams['type']            = this.queryParams['type'];
        if (this.queryParams['amenity_id'])     queryParams['amenity_id']      = this.queryParams['amenity_id'];
        if (this.queryParams['only_unread'])    queryParams['only_unread']     = this.queryParams['only_unread'];
        if (this.queryParams['readStatus'])     queryParams['readStatus']      = this.queryParams['readStatus'];
        if (this.queryParams['periodStartDate'])queryParams['periodStartDate'] = this.queryParams['periodStartDate'];
        if (this.queryParams['periodEndDate'])  queryParams['periodEndDate']   = this.queryParams['periodEndDate'];
        if (this.queryParams['createdAt'])      queryParams['createdAt']       = this.queryParams['createdAt'];
        if (this.queryParams['isPredefined'])   queryParams['isPredefined']    = this.queryParams['isPredefined'];
        if (this.queryParams['company_id'])     queryParams['company_id']      = this.queryParams['company_id'];
        if (this.queryParams['category'])       queryParams['category']        = this.queryParams['category'];
        if (this.queryParams['show_moved_out']) queryParams['show_moved_out']  = this.queryParams['show_moved_out'];
        if (this.queryParams['role'])           queryParams['role']            = this.queryParams['role'];
        if (this.queryParams['corporation'])    queryParams['corporation']     = this.queryParams['corporation'];
        if (this.queryParams['hasPayout'])       queryParams['hasPayout']        = this.queryParams['hasPayout'];
        if (this.queryParams['accountId'])      queryParams['accountId']       = this.queryParams['accountId'];
        if (this.queryParams['group_id'])       queryParams['group_id']        = this.queryParams['group_id'];

        const urlTree = this.router.createUrlTree(
            [],
            {queryParams: queryParams});
        this.router.navigateByUrl(urlTree)
            .then(() => {
                this.routeChanged.emit();
                this.getQueryParams();
                if (!this.isObjectSame(this.currentQuery, this.queryParams) || this.queryParams['tab'] !== 'documents') {
                    this.currentQuery = {...this.queryParams};
                    this.onUrlChangeEmitter.emit(this.queryParams);
                }
            });
    }

    private isObjectSame(first: any, second: any) {
        return JSON.stringify(first) === JSON.stringify(second);
    }

    resetSearchQuery(callback?: Function) {
        if (this.queryParams['query']) {
            this.queryParams['query'] = '';
            this.setQueryParams();
            if (callback) callback();
        }
    }

    /**
     * Updates this.queryParams.query property
     * depending on the current tab
     * or passed parameters
     *
     * @param searchTerm
     * @param tab
     * @param resident
     */
    updateSearchQuery(searchTerm = null, tab?, resident?: boolean) {
        if (resident) {
            this.queryParams.query = searchTerm;
        } else {
            tab = tab || this.queryParams.tab;
            if (searchTerm === null && this.queryParams.query) {
                const queryData = this.queryParams.query.split(':');
                searchTerm = queryData[queryData.length - 1] || '';
            }

            const tabSearchFields = this.queryFields.hasOwnProperty(tab) ? this.queryFields[tab] : [];
            const searchFields = tabSearchFields
                .filter(field => field.isSelected)
                .map(field => field.field)
                .join('|');

            this.queryParams.query = searchFields && searchTerm ? `:${searchFields}:${searchTerm}` : searchTerm;
        }
    }

    resetPageQuery() {
        if (this.queryParams['page']) {
            this.queryParams['page'] = '';
            this.setQueryParams();
        }
    }


    // FIXME: refactoring is needed
    getQueryParamsString(): string {
        const page = this.queryParams['page'] || 1;
        const perPage = this.queryParams['per-page'] || 15;
        const tab = this.queryParams['tab'];

        const building = this.queryParams['building'] ? `&${this.buildingFilter[tab]}=${this.queryParams['building']}` : '';
        const query = this.queryParams['query'] ? `&query=${encodeURIComponent(this.queryParams['query'])}` : '';
        const inOut = this.queryParams['inOut'] ? `&inOut=${this.queryParams['inOut']}` : '';
        const report_type = this.queryParams['report_type'] ? `&${this.getReportTypesQueryString(this.queryParams['report_type'])}` : '';
        const status = this.queryParams['status'] ? `&${this.getStatusQueryString(this.queryParams['status'])}` : '';
        const permissions = this.queryParams['permissions'] ? `&${this.getPermissionsQueryString(this.queryParams['permissions'])}` : '';
        const shared = this.queryParams['is_shared'] ? this.getIsSharedQueryString(this.queryParams['is_shared']) : '';
        const isPublic = this.queryParams['is_public'] ? this.getIsPublicQueryString(this.queryParams['is_public']) : '';
        const orderBy = this.queryParams['order-by'] && this.queryParams['order-by'] != 'undefined' ? `&order-by=${this.queryParams['order-by']}` : '';
        const priority = this.queryParams['priority'] ? `&${this.getPriorityString(this.queryParams['priority'])}` : '';
        const userType = this.queryParams['user_type'] ? `&${this.getUserTypeString(this.queryParams['user_type'])}` : '';
        const type = this.queryParams['type'] ? `&${this.getTypeString(this.queryParams['type'])}` : '';
        const enableStatus = this.queryParams['enable_status'] ? `&enable_status=${this.queryParams['enable_status']}` : '';
        const amenityId = this.queryParams['amenity_id'] ? `&${this.getAmenityIdQueryString(this.queryParams['amenity_id'])}` : '';
        const onlyUnread = this.queryParams['only_unread'] ? '&only-unread=1' : '';
        const readStatus = this.queryParams['readStatus'] ? `&${this.getReadStatusQueryString(this.queryParams['readStatus'])}` : '';
        const periodStartDate = this.queryParams['periodStartDate'] ? `&periodStartDate=>${this.queryParams['periodStartDate']}` : '';
        const periodEndDate = this.queryParams['periodEndDate'] ? `&periodEndDate=<${this.queryParams['periodEndDate']}` : '';
        const createdAt = this.queryParams['createdAt'] ? `&createdAt=${this.queryParams['createdAt']}` : '';
        const isPredefined = this.queryParams['isPredefined'] ? `&isPredefined=${this.queryParams['isPredefined']}` : '';
        const companyId = this.queryParams['company_id'] ? `&${this.getCompanyIdQueryString(this.queryParams['company_id'])}` : '';
        const category = this.queryParams['category'] ? `&${this.getCategoriesString(this.queryParams['category'])}` : '';
        const showMovedOut = this.queryParams['show_moved_out'] ? `&show_moved_out=true` : '';
        const role = this.queryParams['role'] ? `&${this.getOccupancyTypesString(this.queryParams['role'])}` : '';
        const groupId = this.valueToParamString('group_id', this.queryParams['group_id']);

        return `&page=${page}&per-page=${perPage}${building}${query}${inOut}${status}${orderBy}${report_type}${shared}${isPublic}${permissions}${priority}${userType}${type}${enableStatus}${amenityId}${onlyUnread}${readStatus}${periodStartDate}${periodEndDate}${createdAt}${isPredefined}${companyId}${showMovedOut}${role}${category}${groupId}`;
    }

    getIsSharedQueryString(value) {
        if (value.indexOf('1') > -1 && value.indexOf('0') > -1) {
            return '';
        } else if (value.indexOf('0') > -1 && value.indexOf('1') < 0) {
            return `&is_shared=0`;
        } else if (value.indexOf('1') > -1 && value.indexOf('0') < 0) {
            return `&is_shared=1`;
        } else {
            return '';
        }
    }

    getIsPublicQueryString(value) {
        if (value.indexOf('1') > -1 && value.indexOf('0') > -1) {
            return '';
        } else if (value.indexOf('0') > -1 && value.indexOf('1') < 0) {
            return `&is_public=0`;
        } else if (value.indexOf('1') > -1 && value.indexOf('0') < 0) {
            return `&is_public=1`;
        } else {
            return '';
        }
    }

    getPermissionsQueryString(permissions: string) {
        const arr = permissions.split(',');
        let result = '';
        arr.forEach((item: string, i: number) => result += `permissions[${i}]=${item}&`);
        return result.slice(0, -1);
    }

    getStatusQueryString(status: string) {
        const arr = status.split(',');
        let result = '';
        arr.forEach((item: string, i: number) => result += `status[${i}]=${item}&`);
        return result.slice(0, -1);
    }

    getReadStatusQueryString(readStatus: string) {
        const arr = readStatus.split(',');
        let result = '';
        arr.forEach((item: string, i: number) => result += `readStatus[${i}]=${item}&`);
        return result.slice(0, -1);
    }

    getReportTypesQueryString(reportTypes: Array<any>) {
        let result = '';
        if (typeof reportTypes != 'string') {
            reportTypes.forEach((item: string, i: number) => result += `report_type[${i}]=${item}&`);
            return result.slice(0, -1);
        } else
            return `report_type[0]=${reportTypes}&`;
    }

    getAmenityIdQueryString(amenityIds: Array<any>) {
        let result = '';
        if (typeof amenityIds != 'string') {
            amenityIds.forEach((item: string, i: number) => result += `amenity_id[${i}]=${item}&`);
            return result.slice(0, -1);
        } else
            return `amenity_id[0]=${amenityIds}&`;
    }

    getCompanyIdQueryString(companyIds: Array<any>) {
        let result = '';
        if (typeof companyIds != 'string') {
            companyIds.forEach((item: string, i: number) => result += `company_id[${i}]=${item}&`);
            return result.slice(0, -1);
        } else
            return `company_id[0]=${companyIds}&`;
    }

    getPriorityString(priorities: string) {
        let result = '';
        const arr = priorities.split(',');
        arr.forEach((item: string, i: number) => result += `priority[${i}]=${item}&`);
        return result.slice(0, -1);
    }

    getUserTypeString(userTypes: string) {
        let result = '';
        const arr = userTypes.split(',');
        arr.forEach((item: string, i: number) => result += `user_type[${i}]=${item}&`);
        return result.slice(0, -1);
    }

    getTypeString(types: string) {
        let result = '';
        const arr = types.split(',');
        arr.forEach((item: string, i: number) => result += `type[${i}]=${item}&`);
        return result.slice(0, -1);
    }

    orderBy(fieldName: string, event: any) {
        const clickedElement = (event.target.className === 'footable-sort-indicator') ? event.target.parentElement : event.target;
        const parent = clickedElement.parentElement;
        let order = 'asc';

        if (!clickedElement.className) {
            Array.prototype.forEach.call(parent.children, (item) => {
                item.removeAttribute('class');
            });
        }
        if (clickedElement.classList.contains('footable-sorted')) {
            clickedElement.classList.remove('footable-sorted');
            clickedElement.classList.add('footable-sorted-desc');
            order = 'desc';
        } else {
            clickedElement.classList.remove('footable-sorted-desc');
            clickedElement.classList.add('footable-sorted');
            order = 'asc';
        }

        const orderBy = `${fieldName}|${order}`;
        this.setQueryParams(null, null, null, orderBy);
    }

    resetQueryParams() {
        if (!UrlService.EnableResetQueryParams) { return; } // Todo: проблема с Quick Search, с других страниц не происходит редирект на нужную страницу!
        this.queryParams = {};
        const urlTree = this.router.createUrlTree([], {});
        this.router.navigateByUrl(urlTree);
    }

    getOccupancyTypesString(occupancyTypes: Array<any>) {
        let result = '';
        if (typeof occupancyTypes != 'string') {
            occupancyTypes.forEach((item: string, i: number) => result += `role[${i}]=${item}&`);
            return result.slice(0, -1);
        } else
            return `role[0]=${occupancyTypes}&`;
    }

    getCategoriesString(categories: Array<any>) {
        let result = '';
        if (typeof categories != 'string') {
            categories.forEach((item: string, i: number) => result += `category[${i}]=${item}&`);
            return result.slice(0, -1);
        } else
            return `category[0]=${categories}&`;
    }

    valueToParamString(key, value) {
        if (value && typeof value === 'string') {
            return `&${key}=${value}`;
        } else if (Array.isArray(value) && value.length) {
            return '&' + value.map((id, i) => `${key}[${i}]=${id}`).join('&');
        }

        return '';
    }
}



// WEBPACK FOOTER //
// /app/src/app/services/url.service.ts